// @ts-strict-ignore
import * as Sentry from '@sentry/react';
import type { SeverityLevel } from '@sentry/types';
import { datadogRum } from '@datadog/browser-rum';
import type { Area, Rule } from './teamOwnersByUrl';
import {
  advanceRules, buildRules, connectRules, dpiRules, improveRules, operateRules, platformRules,
} from './teamOwnersByUrl';

export class ResponseException extends Error {
  response: any;

  requestDetails: any;

  responseError: any;

  constructor(response, requestDetails = null, errorBody = null) {
    let responseError = null;

    // The response body may have a nested error object
    if (errorBody && errorBody.error) {
      responseError = errorBody.error;
    } else if (errorBody) {
      responseError = errorBody;
    }

    let errorMessage = '';
    if (responseError && responseError.message) {
      errorMessage = `: ${responseError.message}`;
    }

    super(`${requestDetails?.method} ${response?.status} - ${requestDetails?.url}: ${response?.statusText} ${errorMessage}`);
    this.response = response;
    this.name = 'ResponseException';
    if (requestDetails) this.requestDetails = requestDetails;
    if (responseError) {
      this.responseError = responseError;
    }
  }
}

export function sentryBeforeSendProcessing(
  event,
  hint,
) {
  const exception = hint.originalException;

  if (exception instanceof ResponseException
    && exception.responseError?.minor_code >= 4011 && exception.responseError?.minor_code <= 4033) {
    event.fingerprint = ['responseexception-40x-error'];
  }

  if (exception?.message?.match(/Failed to fetch|NetworkError/i)
    && exception instanceof TypeError) {
    // if failed for obviously network error, we drop it
    return null;
  }

  return event;
}

export function matchUrlWithCoreFeatureRoutes(
  url,
) {
  const listOfCoreFeatures = [
    'journeys/',
    'journeys/applications/',
    'entities/applications/',
    'evaluations/applications/',
    'applications/',
    'workflows/',
    'evaluations/',
    'entities/',
    'groups/',
    'cases/',
    'sar/',
    'outgoing-webhooks/',
  ];
  const routeMatch = url.match(/applications\/([a-zA-Z0-9]{32,})|journeys\/applications\/|journeys\/J-([a-zA-Z0-9]+)\/versions\/|journeys\/J-([a-zA-Z0-9]+)\/applications\/|workflows\/||evaluations\/|entities\/|groups\/|cases\/|sar\/|outgoing-webhooks\//ig);
  const coreFeatureRouteMatches = routeMatch
    ? routeMatch.reduce((all, match) => {
      if (match !== '') {
        const cleanedPath = match.replace(/\/(?!journeys|v3|dashboard|workflows|applications|evaluations|entities|groups|cases|sar|versions|update)([a-zA-Z0-9-_]{5,})\/?/ig, '/');

        if (listOfCoreFeatures.filter(featureRoute => cleanedPath.match(new RegExp(featureRoute, 'ig')))?.length) {
          all.push(cleanedPath);
        }
      }

      return all;
    }, [])
    : [];

  return coreFeatureRouteMatches;
}

export function getSessionReplayUrl() {
  let sessionUrlAtTime;
  try {
    sessionUrlAtTime = datadogRum.getSessionReplayLink();
  } catch (error) {
    sessionUrlAtTime = undefined;
  }

  return sessionUrlAtTime;
}

interface EventTags {
  session_replay_url?: string;
  dashboard_area?: string;
  enriched?: boolean;
}

interface EnrichmentEvent {
  tags: EventTags;
  level: string;
}

export function enrichUnhandledExceptionData(
  data,
): EnrichmentEvent {
  const event: EnrichmentEvent = {
    tags: {},
    level: 'error',
  };

  event.tags.session_replay_url = getSessionReplayUrl();

  if (data?.request?.url) {
    const coreFeatureRouteMatches = matchUrlWithCoreFeatureRoutes(data?.request?.url);
    if (coreFeatureRouteMatches?.length) {
      if (coreFeatureRouteMatches[0] === 'evaluations/'
        && data.request.url.includes('evaluations/run')) {
        delete event.tags.dashboard_area;
      } else if (coreFeatureRouteMatches[0] === 'applications/') {
        event.tags.dashboard_area = 'workflows';
      } else {
        event.tags.dashboard_area = coreFeatureRouteMatches[0].slice(0, -1);
      }

      event.tags.enriched = true;
    }
  }

  return event;
}

// this works in conjunction with the "classify" function below to assign a "Something went wrong" error to the correct team
export function makeUrlClassifier(rules: Rule[]) {
  // Precompute for speed & predictable precedence
  const exact = new Map<string, Area>();
  const prefixes: Array<{ prefix: string; area: Area }> = [];
  const regexes: Array<{ pattern: RegExp; area: Area }> = [];

  for (const r of rules) {
    if (r.type === 'exact') {
      exact.set(r.path, r.area);
    } else if (r.type === 'startsWith') {
      prefixes.push({ prefix: r.prefix, area: r.area });
    } else {
      regexes.push({ pattern: r.pattern, area: r.area });
    }
  }

  // Longest-prefix-first so more specific prefixes win
  prefixes.sort((a, b) => b.prefix.length - a.prefix.length);

  return function classify(startPath: string): Area {
    // Assume `path` is already like "/v3/dashboard/…"
    const path = startPath.length > 1 && startPath.endsWith('/')
      ? startPath.slice(0, -1)
      : startPath;

    // 1) exact
    const exactHit = exact.get(path);
    if (exactHit) return exactHit;

    // 2) regex (more specific patterns should override generic prefixes)
    for (const r of regexes) {
      if (r.pattern.test(path)) return r.area;
    }
    // 3) longest prefix
    for (const p of prefixes) {
      if (path.startsWith(p.prefix)) return p.area;
    }

    // default to operate as general FE owners
    return 'operate';
  };
}

const classify = makeUrlClassifier([
  ...platformRules,
  ...operateRules,
  ...dpiRules,
  ...buildRules,
  ...advanceRules,
  ...connectRules,
  ...improveRules,
]);


/**
 * calls Sentry.captureException after processing exception data for either
 * enriching data or adjusting the severity level.
 * @param {*} exception exception object
 * @param {*} payload additional data in an object
 * @param {*} customLevel custom severity level of errors
 */
export function captureExceptionsToSentry(
  exception,
  payload = {},
  customLevel: SeverityLevel | '' = '',
) {
  /**
   * Exclude API/network errors from sentry logging.
   * They should be logged on the API side instead, where the root cause can be more easily traced.
   */
  if (exception instanceof ResponseException) {
    return true;
  }

  const extraPayload = Object.keys(payload)?.length ? payload : exception?.requestDetails || {};

  return Sentry.withScope(scope => {
    scope.setTag('enriched', true);
    // unless typeerror with an obvious error that may be caused by faulty codes, it's set to warning
    if (!exception?.message?.match(/Cannot read|is not a function|is undefined/i) && exception instanceof TypeError === false) {
      scope.setLevel('warning');
    }

    const originated = extraPayload?.originatedUrl;
    if (originated) {
      scope.setTag('originated_from', originated);
    }

    const sessionUrl = getSessionReplayUrl();
    if (sessionUrl) {
      scope.setTag('session_replay_url', sessionUrl);
    }

    const rawUrl = extraPayload?.url || window.location.pathname;
    let pathForClassification = rawUrl;
    try {
      pathForClassification = new URL(rawUrl, window.location.origin).pathname;
    } catch { /* noop */ }

    if (extraPayload?.url) {
      scope.setTag('request_url', rawUrl);
      scope.setTag('team_owner', classify(pathForClassification));
    }

    const coreFeatureRouteMatches = matchUrlWithCoreFeatureRoutes(rawUrl);

    if (coreFeatureRouteMatches?.length) {
      if (extraPayload?.pageCrashed) {
        scope.setLevel('fatal');
      } else {
        scope.setLevel('error');
      }

      if (coreFeatureRouteMatches[0] === 'applications/') {
        scope.setTag('dashboard_area', 'workflows');
      } else {
        scope.setTag('dashboard_area', coreFeatureRouteMatches[0].slice(0, -1));
      }
    }

    if (customLevel !== '') {
      scope.setLevel(customLevel as SeverityLevel);
    }

    // Add ErrorBoundary-specific tags
    if (extraPayload?.errorScreen) {
      scope.setTag('error_screen', extraPayload.errorScreen);
    }
    if (extraPayload?.errorComponent) {
      scope.setTag('error_component', extraPayload.errorComponent);
    }

    Sentry.captureException(exception, { extra: { ...extraPayload } });
    return true;
  });
}
